/**
 * WcOrderDetail — Full WooCommerce order detail with CRUD actions
 * Status changes, notes, complaints, tracking, items, customer data
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  wpApi,
  WcOrderDetail as WcOrderDetailType,
} from '../services/wordpressApi';
import {
  ArrowLeft, RefreshCw, AlertTriangle, User, MapPin, Package,
  CreditCard, Truck, MessageSquare, Send, ChevronDown, ChevronUp,
  Phone, Mail, Clock, CheckCircle, XCircle, ExternalLink, Copy, Edit3, Save,
} from 'lucide-react';
import '../styles/WcOrderDetail.css';

// ── Status config ──

interface StatusConfig {
  label: string;
  color: string;
  bg: string;
  emoji: string;
}

const STATUS_MAP: Record<string, StatusConfig> = {
  processing:  { label: 'Przetwarzane', color: '#f59e0b', bg: '#fef3c7', emoji: '🔄' },
  'on-hold':   { label: 'Wstrzymane',   color: '#ef4444', bg: '#fee2e2', emoji: '⏸️' },
  completed:   { label: 'Zrealizowane', color: '#10b981', bg: '#d1fae5', emoji: '✅' },
  pending:     { label: 'Oczekujące',   color: '#6b7280', bg: '#f3f4f6', emoji: '⏳' },
  cancelled:   { label: 'Anulowane',    color: '#6b7280', bg: '#f3f4f6', emoji: '❌' },
  refunded:    { label: 'Zwrócone',     color: '#8b5cf6', bg: '#ede9fe', emoji: '↩️' },
  failed:      { label: 'Nieudane',     color: '#dc2626', bg: '#fee2e2', emoji: '⛔' },
};

const getStatus = (s: string): StatusConfig =>
  STATUS_MAP[s] || { label: s, color: '#6b7280', bg: '#f3f4f6', emoji: '❓' };

const TRACKABLE_STATUSES = ['processing', 'on-hold', 'pending', 'completed', 'cancelled', 'refunded', 'failed'];

// ── Format helpers ──

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '—';
  const d = new Date(dateStr.replace(' ', 'T'));
  const day = d.getDate().toString().padStart(2, '0');
  const month = (d.getMonth() + 1).toString().padStart(2, '0');
  const hours = d.getHours().toString().padStart(2, '0');
  const mins = d.getMinutes().toString().padStart(2, '0');
  return `${day}.${month}.${d.getFullYear()} ${hours}:${mins}`;
}

function formatMoney(amount: string | number, currency = 'PLN'): string {
  const n = typeof amount === 'string' ? parseFloat(amount) : amount;
  return `${n.toFixed(2)} ${currency}`;
}

function timeAgo(dateStr: string): string {
  const d = new Date(dateStr.replace(' ', 'T'));
  const now = new Date();
  const diff = Math.floor((now.getTime() - d.getTime()) / 1000);
  if (diff < 60) return 'przed chwilą';
  if (diff < 3600) return `${Math.floor(diff / 60)} min temu`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} godz. temu`;
  return `${Math.floor(diff / 86400)} dni temu`;
}

function formatAddress(addr: { first_name: string; last_name: string; company: string; address_1: string; address_2: string; city: string; postcode: string; country: string }): string[] {
  const lines: string[] = [];
  if (addr.company) lines.push(addr.company);
  lines.push(`${addr.first_name} ${addr.last_name}`);
  if (addr.address_1) lines.push(addr.address_1);
  if (addr.address_2) lines.push(addr.address_2);
  lines.push(`${addr.postcode} ${addr.city}`);
  if (addr.country && addr.country !== 'PL') lines.push(addr.country);
  return lines.filter(Boolean);
}

// ── Component ──

export function WcOrderDetail() {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const notesEndRef = useRef<HTMLDivElement>(null);

  // State
  const [order, setOrder] = useState<WcOrderDetailType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  // Status change
  const [showStatusPicker, setShowStatusPicker] = useState(false);
  const [statusNote, setStatusNote] = useState('');
  const [changingStatus, setChangingStatus] = useState(false);

  // Notes
  const [noteText, setNoteText] = useState('');
  const [addingNote, setAddingNote] = useState(false);

  // Complaint editing
  const [editingComplaint, setEditingComplaint] = useState(false);
  const [complaintText, setComplaintText] = useState('');
  const [savingComplaint, setSavingComplaint] = useState(false);

  // Sections collapse
  const [sectionsOpen, setSectionsOpen] = useState<Record<string, boolean>>({
    customer: true,
    items: true,
    totals: true,
    shipping: true,
    complaint: true,
    notes: true,
  });

  const toggleSection = (key: string) => {
    setSectionsOpen(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // ── Fetch order ──
  const fetchOrder = useCallback(async () => {
    if (!orderId) return;
    try {
      setError(null);
      const data = await wpApi.getWcOrderDetail(Number(orderId));
      setOrder(data);
      setComplaintText(data.complaint_text || '');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Błąd ładowania zamówienia');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [orderId]);

  useEffect(() => {
    fetchOrder();
  }, [fetchOrder]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchOrder();
  };

  // ── Status change ──
  const handleStatusChange = async (newStatus: string) => {
    if (!order || changingStatus) return;
    setChangingStatus(true);
    try {
      await wpApi.updateOrderStatus(order.id, newStatus, statusNote || undefined);
      setShowStatusPicker(false);
      setStatusNote('');
      await fetchOrder();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Błąd zmiany statusu');
    } finally {
      setChangingStatus(false);
    }
  };

  // ── Add note ──
  const handleAddNote = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!order || !noteText.trim() || addingNote) return;
    setAddingNote(true);
    try {
      await wpApi.addOrderNote(order.id, noteText.trim());
      setNoteText('');
      await fetchOrder();
      // Scroll to bottom of notes
      setTimeout(() => notesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Błąd dodawania notatki');
    } finally {
      setAddingNote(false);
    }
  };

  // ── Save complaint ──
  const handleSaveComplaint = async () => {
    if (!order || savingComplaint) return;
    setSavingComplaint(true);
    try {
      await wpApi.updateOrderMeta(order.id, 'order_complaint', complaintText);
      setEditingComplaint(false);
      await fetchOrder();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Błąd zapisu reklamacji');
    } finally {
      setSavingComplaint(false);
    }
  };

  // ── Copy to clipboard ──
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).catch(() => {});
  };

  // ── Loading / Error ──
  if (loading) {
    return (
      <div className="wcd-loading">
        <div className="pulse-dot" />
        <span>Ładowanie zamówienia…</span>
      </div>
    );
  }

  if (error && !order) {
    return (
      <div className="wcd-error">
        <AlertTriangle size={40} />
        <p>{error}</p>
        <button className="btn-refresh" onClick={handleRefresh}>Spróbuj ponownie</button>
        <button className="btn-back-link" onClick={() => navigate('/wc-orders')}>
          ← Wróć do listy
        </button>
      </div>
    );
  }

  if (!order) return null;

  const status = getStatus(order.status);
  const marginAmount = parseFloat(order.total) - order.supplier_cost_total;
  const marginPct = order.supplier_cost_total > 0
    ? ((marginAmount / parseFloat(order.total)) * 100).toFixed(1)
    : null;

  return (
    <div className="wcd-page">
      {/* ── Header ── */}
      <div className="wcd-header">
        <button className="wcd-back" onClick={() => navigate('/wc-orders')}>
          <ArrowLeft size={20} />
        </button>
        <div className="wcd-header-info">
          <h1>#{order.number}</h1>
          <span className="wcd-header-date">{formatDate(order.date_created)}</span>
        </div>
        <button
          className={`btn-icon-refresh ${refreshing ? 'spinning' : ''}`}
          onClick={handleRefresh}
          title="Odśwież"
        >
          <RefreshCw size={18} />
        </button>
      </div>

      {/* ── Status Card ── */}
      <div className="wcd-section wcd-status-card">
        <div className="wcd-status-row">
          <span
            className="wcd-status-badge"
            style={{ color: status.color, background: status.bg }}
          >
            {status.emoji} {status.label}
          </span>
          <button
            className="wcd-status-change-btn"
            onClick={() => setShowStatusPicker(!showStatusPicker)}
          >
            Zmień {showStatusPicker ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>
        </div>

        {showStatusPicker && (
          <div className="wcd-status-picker">
            <div className="status-picker-options">
              {TRACKABLE_STATUSES.filter(s => s !== order.status).map(s => {
                const sc = getStatus(s);
                return (
                  <button
                    key={s}
                    className="status-option"
                    style={{ borderColor: sc.color }}
                    onClick={() => !changingStatus && handleStatusChange(s)}
                    disabled={changingStatus}
                  >
                    <span>{sc.emoji}</span>
                    <span>{sc.label}</span>
                  </button>
                );
              })}
            </div>
            <input
              type="text"
              className="status-note-input"
              placeholder="Notatka do zmiany statusu (opcjonalnie)"
              value={statusNote}
              onChange={e => setStatusNote(e.target.value)}
            />
            {changingStatus && (
              <div className="status-changing">
                <div className="pulse-dot small" /> Zmieniam status…
              </div>
            )}
          </div>
        )}

        <div className="wcd-dates-grid">
          <div className="wcd-date-item">
            <Clock size={12} />
            <span>Utworzono: {formatDate(order.dates.created)}</span>
          </div>
          {order.dates.paid && (
            <div className="wcd-date-item">
              <CheckCircle size={12} />
              <span>Opłacono: {formatDate(order.dates.paid)}</span>
            </div>
          )}
          {order.dates.completed && (
            <div className="wcd-date-item">
              <CheckCircle size={12} />
              <span>Zakończono: {formatDate(order.dates.completed)}</span>
            </div>
          )}
        </div>
      </div>

      {/* ── Customer Card ── */}
      <div className="wcd-section">
        <div className="wcd-section-header" onClick={() => toggleSection('customer')}>
          <div className="wcd-section-title">
            <User size={16} /> Klient
          </div>
          {sectionsOpen.customer ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </div>
        {sectionsOpen.customer && (
          <div className="wcd-section-body">
            <div className="wcd-customer-name">{order.customer_name}</div>

            {order.customer_phone && (
              <a href={`tel:${order.customer_phone}`} className="wcd-contact-link">
                <Phone size={14} />
                <span>{order.customer_phone}</span>
              </a>
            )}
            {order.customer_email && (
              <a href={`mailto:${order.customer_email}`} className="wcd-contact-link">
                <Mail size={14} />
                <span>{order.customer_email}</span>
              </a>
            )}

            {order.customer_note && (
              <div className="wcd-customer-note">
                <MessageSquare size={12} />
                <span>"{order.customer_note}"</span>
              </div>
            )}

            <div className="wcd-addresses">
              <div className="wcd-address-block">
                <div className="wcd-address-label">
                  <CreditCard size={12} /> Fakturowanie
                </div>
                {formatAddress(order.billing).map((line, i) => (
                  <div key={i} className="wcd-address-line">{line}</div>
                ))}
              </div>
              <div className="wcd-address-block">
                <div className="wcd-address-label">
                  <MapPin size={12} /> Wysyłka
                </div>
                {formatAddress(order.shipping).map((line, i) => (
                  <div key={i} className="wcd-address-line">{line}</div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ── Items Card ── */}
      <div className="wcd-section">
        <div className="wcd-section-header" onClick={() => toggleSection('items')}>
          <div className="wcd-section-title">
            <Package size={16} /> Produkty ({order.items_count})
          </div>
          {sectionsOpen.items ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </div>
        {sectionsOpen.items && (
          <div className="wcd-section-body wcd-items-body">
            {order.items.map(item => (
              <div key={item.id} className="wcd-item">
                {item.image_url && (
                  <img
                    src={item.image_url}
                    alt=""
                    className="wcd-item-img"
                    loading="lazy"
                  />
                )}
                <div className="wcd-item-info">
                  <div className="wcd-item-name">
                    {item.product_url ? (
                      <a href={item.product_url} target="_blank" rel="noopener noreferrer">
                        {item.name}
                        <ExternalLink size={10} />
                      </a>
                    ) : (
                      item.name
                    )}
                  </div>
                  <div className="wcd-item-meta">
                    {item.sku && <span className="item-sku">SKU: {item.sku}</span>}
                    <span className="item-qty">×{item.quantity}</span>
                  </div>
                  <div className="wcd-item-prices">
                    <span className="item-price">{formatMoney(item.total)}</span>
                    {item.supplier_cost != null && (
                      <span className="item-cost">Koszt: {formatMoney(item.supplier_cost)}</span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Totals Card ── */}
      <div className="wcd-section">
        <div className="wcd-section-header" onClick={() => toggleSection('totals')}>
          <div className="wcd-section-title">
            <CreditCard size={16} /> Podsumowanie
          </div>
          {sectionsOpen.totals ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </div>
        {sectionsOpen.totals && (
          <div className="wcd-section-body">
            <div className="wcd-totals">
              <div className="wcd-total-row">
                <span>Produkty</span>
                <span>{formatMoney(order.totals.subtotal)}</span>
              </div>
              <div className="wcd-total-row">
                <span>Wysyłka</span>
                <span>{formatMoney(order.totals.shipping)}</span>
              </div>
              {parseFloat(order.totals.discount) > 0 && (
                <div className="wcd-total-row discount">
                  <span>Rabat</span>
                  <span>-{formatMoney(order.totals.discount)}</span>
                </div>
              )}
              {order.coupons.length > 0 && (
                <div className="wcd-total-row coupon">
                  <span>Kupony: {order.coupons.map(c => c.code).join(', ')}</span>
                  <span></span>
                </div>
              )}
              <div className="wcd-total-row total">
                <span>Razem</span>
                <span>{formatMoney(order.total, order.currency)}</span>
              </div>
            </div>

            <div className="wcd-payment-info">
              <span className="wcd-payment-method">
                {order.payment_method_title}
              </span>
              {order.transaction_id && (
                <span className="wcd-transaction-id">
                  ID: {order.transaction_id}
                  <button className="btn-copy" onClick={() => copyToClipboard(order.transaction_id)}>
                    <Copy size={10} />
                  </button>
                </span>
              )}
            </div>

            {order.supplier_cost_total > 0 && (
              <div className="wcd-margin-card">
                <div className="wcd-margin-row">
                  <span>Koszt dostawcy</span>
                  <span>{formatMoney(order.supplier_cost_total)}</span>
                </div>
                <div className="wcd-margin-row margin">
                  <span>Marża</span>
                  <span>
                    {formatMoney(marginAmount)} {marginPct && <small>({marginPct}%)</small>}
                  </span>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Shipping / Tracking Card ── */}
      <div className="wcd-section">
        <div className="wcd-section-header" onClick={() => toggleSection('shipping')}>
          <div className="wcd-section-title">
            <Truck size={16} /> Wysyłka & Tracking
          </div>
          {sectionsOpen.shipping ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </div>
        {sectionsOpen.shipping && (
          <div className="wcd-section-body">
            {order.shipping_method && (
              <div className="wcd-shipping-method">
                <Truck size={14} />
                <span>{order.shipping_method}</span>
              </div>
            )}

            {order.tracking_number && (
              <div className="wcd-tracking">
                <div className="wcd-tracking-label">Nr przesyłki:</div>
                <div className="wcd-tracking-number">
                  <span>{order.tracking_number}</span>
                  <button className="btn-copy" onClick={() => copyToClipboard(order.tracking_number!)}>
                    <Copy size={12} />
                  </button>
                </div>
                {order.tracking_status && (
                  <div className="wcd-tracking-status">
                    Status: <strong>{order.tracking_status}</strong>
                  </div>
                )}
              </div>
            )}

            {order.supplier_order_id && (
              <div className="wcd-supplier-order">
                <div className="wcd-supplier-label">Zamówienie u dostawcy:</div>
                <div className="wcd-supplier-value">{order.supplier_order_id}</div>
              </div>
            )}

            {!order.tracking_number && !order.supplier_order_id && (
              <div className="wcd-no-tracking">
                Brak danych o wysyłce
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Complaint Card ── */}
      {(order.has_complaint || editingComplaint) && (
        <div className="wcd-section wcd-complaint-section">
          <div className="wcd-section-header" onClick={() => toggleSection('complaint')}>
            <div className="wcd-section-title complaint">
              <AlertTriangle size={16} /> Reklamacja
            </div>
            {sectionsOpen.complaint ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </div>
          {sectionsOpen.complaint && (
            <div className="wcd-section-body">
              {editingComplaint ? (
                <div className="wcd-complaint-edit">
                  <textarea
                    value={complaintText}
                    onChange={e => setComplaintText(e.target.value)}
                    placeholder="Opis reklamacji…"
                    rows={3}
                  />
                  <div className="wcd-complaint-actions">
                    <button
                      className="btn-save"
                      onClick={handleSaveComplaint}
                      disabled={savingComplaint}
                    >
                      <Save size={14} />
                      {savingComplaint ? 'Zapisuję…' : 'Zapisz'}
                    </button>
                    <button
                      className="btn-cancel"
                      onClick={() => {
                        setEditingComplaint(false);
                        setComplaintText(order.complaint_text || '');
                      }}
                    >
                      Anuluj
                    </button>
                  </div>
                </div>
              ) : (
                <div className="wcd-complaint-view">
                  <span className="complaint-text">{order.complaint_text || 'Brak opisu'}</span>
                  <button className="btn-edit-complaint" onClick={() => setEditingComplaint(true)}>
                    <Edit3 size={14} /> Edytuj
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── Add Complaint button (if none) ── */}
      {!order.has_complaint && !editingComplaint && (
        <button
          className="wcd-add-complaint-btn"
          onClick={() => {
            setEditingComplaint(true);
            setComplaintText('');
          }}
        >
          <AlertTriangle size={14} /> Dodaj reklamację
        </button>
      )}

      {/* ── Notes Card ── */}
      <div className="wcd-section wcd-notes-section">
        <div className="wcd-section-header" onClick={() => toggleSection('notes')}>
          <div className="wcd-section-title">
            <MessageSquare size={16} /> Notatki ({order.notes.length})
          </div>
          {sectionsOpen.notes ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </div>
        {sectionsOpen.notes && (
          <div className="wcd-section-body">
            <div className="wcd-notes-timeline">
              {order.notes.map(note => (
                <div
                  key={note.id}
                  className={`wcd-note ${note.is_system ? 'system' : ''} ${note.customer_note ? 'customer' : ''}`}
                >
                  <div className="wcd-note-header">
                    <span className="wcd-note-author">
                      {note.customer_note ? '👤 Klient' : note.is_system ? '⚙️ System' : '👨‍💼 Manager'}
                    </span>
                    <span className="wcd-note-time" title={formatDate(note.date_created)}>
                      {timeAgo(note.date_created)}
                    </span>
                  </div>
                  <div
                    className="wcd-note-content"
                    dangerouslySetInnerHTML={{ __html: note.content }}
                  />
                </div>
              ))}
              <div ref={notesEndRef} />
            </div>

            {/* Add note form */}
            <form className="wcd-add-note" onSubmit={handleAddNote}>
              <input
                type="text"
                placeholder="Dodaj notatkę wewnętrzną…"
                value={noteText}
                onChange={e => setNoteText(e.target.value)}
                disabled={addingNote}
              />
              <button
                type="submit"
                disabled={!noteText.trim() || addingNote}
                className="btn-send-note"
              >
                {addingNote ? <div className="pulse-dot small" /> : <Send size={16} />}
              </button>
            </form>
          </div>
        )}
      </div>

      {/* ── Error toast ── */}
      {error && (
        <div className="wcd-toast">
          <AlertTriangle size={14} />
          <span>{error}</span>
          <button onClick={() => setError(null)}>
            <XCircle size={14} />
          </button>
        </div>
      )}
    </div>
  );
}
